import { IonIcon, IonModal } from "@ionic/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/button";
import { api } from "@/lib/backend";
import type { TemplateStructureItemEntity } from "@/lib/backend/api";
import { Camera } from "@/lib/capacitor/camera";
import { useMe } from "@/lib/hooks/use-me";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import {
  arrowUpIcon,
  checkIcon,
  chevronUpIcon,
  documentTextIcon,
  photoIcon,
  plusIcon,
  sparklesIcon,
  xMarkIcon,
} from "@/lib/icons/@heroicons/react/24/outline";
import { BoomerIcon } from "@/lib/icons/boomer.icon";
import { WhatsappIcon } from "@/lib/icons/whatsapp.icon";
import type { TypedConversation } from "@/lib/query/conversation/conversation.types";
import { useMessageTemplateVariables } from "@/lib/query/message-template";
import { useSendMessage } from "@/lib/query/message/use-send-message";
import { cx } from "@/lib/style/cva.config";
import { TemplateSelectionModal } from "./template-selection-modal";

type Platform = "Whatsapp" | "Boomer";

interface MessageComposerProps {
  conversationId: string;
  conversation: TypedConversation;
  onMessageSent?: () => void;
  isCollapsed?: boolean;
  onExpand?: () => void;
  onTypingStart?: () => void;
  onTypingStop?: () => void;
  onHeightChange?: (height: number) => void;
}

export function MessageComposer({
  conversationId,
  conversation,
  onMessageSent,
  isCollapsed = false,
  onExpand,
  onTypingStart,
  onTypingStop,
  onHeightChange,
}: MessageComposerProps) {
  const composerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const haptics = usePreferredHaptics();
  const { isAdmin } = useMe();
  const [input, setInput] = useState("");
  const [platform, setPlatform] = useState<Platform>(() => {
    // Default to conversation channel or Boomer
    if (conversation.channel === "WHATSAPP") {
      return "Whatsapp";
    }
    return "Boomer";
  });
  const [isPlatformModalOpen, setIsPlatformModalOpen] = useState(false);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  // Staged media: uploaded but not yet sent
  const [stagedMedia, setStagedMedia] = useState<{
    mediaUrl: string;
    previewUrl: string;
  } | null>(null);
  // Template selection state (admin only)
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateStructureItemEntity | null>(
    null,
  );

  const modalRef = useRef<HTMLIonModalElement>(null);
  const attachmentModalRef = useRef<HTMLIonModalElement>(null);

  const sendMessage = useSendMessage();

  // Fetch template variables for rendering preview
  const { data: variablesData } = useMessageTemplateVariables();

  // Create a map of variableName -> friendlyName for quick lookup
  const variableMap = useMemo(() => {
    const map = new Map<string, string>();
    variablesData?.items?.forEach((variable) => {
      map.set(variable.variableName, variable.friendlyName);
    });
    return map;
  }, [variablesData?.items]);

  // Typing indicator state
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const TYPING_TIMEOUT_MS = 2000; // Stop typing after 2 seconds of inactivity

  // Handle typing start/stop
  const handleTypingStart = useCallback(() => {
    if (!isTyping) {
      setIsTyping(true);
      onTypingStart?.();
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout to stop typing
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      onTypingStop?.();
    }, TYPING_TIMEOUT_MS);
  }, [isTyping, onTypingStart, onTypingStop]);

  const handleTypingStop = useCallback(() => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
    if (isTyping) {
      setIsTyping(false);
      onTypingStop?.();
    }
  }, [isTyping, onTypingStop]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Report height changes to parent
  useEffect(() => {
    if (!composerRef.current || !onHeightChange) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        onHeightChange(entry.contentRect.height);
      }
    });

    observer.observe(composerRef.current);
    // Report initial height
    onHeightChange(composerRef.current.offsetHeight);

    return () => observer.disconnect();
  }, [onHeightChange]);

  const canSendFreeform = conversation.canSendFreeform !== false;
  const isDisabled = (!canSendFreeform && platform !== "Boomer") || sendMessage.isPending;

  // Can send if there's text, staged media, or a selected template
  const canSend = (input.trim() || stagedMedia || selectedTemplate) && !isDisabled;

  // Parse template text and render with styled tokens
  const renderTemplateText = (text: string): React.ReactNode => {
    const parts = text.split(/(\{\{[^}]+\}\})/g);

    return parts.map((part, index) => {
      const variableMatch = part.match(/^\{\{([^}]+)\}\}$/);
      if (variableMatch) {
        const variableName = variableMatch[1];
        const friendlyName = variableMap.get(variableName) || variableName;
        return (
          <span
            key={index}
            className="inline-flex items-center rounded bg-brand-100 px-1 py-0.5 text-xs font-medium text-brand-700"
          >
            {friendlyName}
          </span>
        );
      }
      // Handle newlines in text
      return part.split("\n").map((line, lineIndex, arr) => (
        <span key={`${index}-${lineIndex}`}>
          {line}
          {lineIndex < arr.length - 1 && <br />}
        </span>
      ));
    });
  };

  const handleClearTemplate = () => {
    haptics.impact();
    setSelectedTemplate(null);
  };

  const handleSend = () => {
    if (!canSend) {
      return;
    }

    // Stop typing indicator when sending
    handleTypingStop();

    haptics.impact();
    sendMessage.mutate(
      {
        conversationId,
        body: selectedTemplate ? "" : input.trim(), // Empty body when using template
        platform: stagedMedia ? "Boomer" : platform, // Media always goes through Boomer
        mediaUrls: stagedMedia ? [stagedMedia.mediaUrl] : undefined,
        messageTemplateId: selectedTemplate?.id, // Include template ID if selected
      },
      {
        onSuccess: () => {
          setInput("");
          setStagedMedia(null);
          setSelectedTemplate(null); // Clear selected template
          onMessageSent?.();
        },
      },
    );
  };

  const handleRemoveStagedMedia = () => {
    haptics.impact();
    setStagedMedia(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInput(value);

    // Trigger typing indicator when user types
    if (value.length > 0) {
      handleTypingStart();
    } else {
      handleTypingStop();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePlatformSelect = (p: Platform) => {
    haptics.impact();
    setPlatform(p);
    setIsPlatformModalOpen(false);
  };

  const openPlatformModal = () => {
    haptics.impact();
    setIsPlatformModalOpen(true);
  };

  const openAttachmentModal = () => {
    haptics.impact();
    setIsAttachmentModalOpen(true);
  };

  const handlePickImage = async () => {
    haptics.impact();
    setIsAttachmentModalOpen(false);

    try {
      // Request permissions first
      const permissions = await Camera.checkPermissions();
      if (permissions.photos !== "granted") {
        const requested = await Camera.requestPermissions(["photos"]);
        if (requested.photos !== "granted") {
          toast.error(t("routes:conversations.photo-permission-denied"));
          return;
        }
      }

      // Pick image from gallery
      const photo = await Camera.getPhoto({
        quality: 80,
        resultType: Camera.ResultType.Uri,
        source: Camera.Source.Photos,
      });

      if (!photo.webPath) {
        return;
      }

      setIsUploadingMedia(true);

      // Keep the local preview URL
      const previewUrl = photo.webPath;

      // Fetch the image as a blob
      const response = await fetch(photo.webPath);
      const blob = await response.blob();

      // Create a File object from the blob
      const fileName = `photo_${Date.now()}.${photo.format || "jpeg"}`;
      const file = new File([blob], fileName, {
        type: `image/${photo.format || "jpeg"}`,
      });

      // Upload to the backend
      const uploadResponse = await api.file.uploadConversationMedia(conversationId, { file });
      const mediaUrl = uploadResponse.data.mediaUrl;

      // Stage the media for preview (don't send yet)
      setStagedMedia({
        mediaUrl,
        previewUrl,
      });
    } catch (error) {
      // User cancelled or error occurred
      if ((error as Error)?.message?.includes("User cancelled")) {
        return;
      }
      toast.error(t("routes:conversations.image-upload-failed"));
    } finally {
      setIsUploadingMedia(false);
    }
  };

  const PlatformIcon = platform === "Boomer" ? BoomerIcon : WhatsappIcon;

  // Expand when user interacts with the composer
  const handleExpand = () => {
    if (isCollapsed && onExpand) {
      onExpand();
    }
  };

  // Stop typing when user leaves the input
  const handleBlur = () => {
    handleTypingStop();
  };

  return (
    <div ref={composerRef} className="fixed inset-x-0 bottom-0 z-10" onClick={handleExpand}>
      {/* Rounded top section */}
      <div
        className={cx(
          "border-t border-bordercolor-primary bg-white transition-all duration-200",
          "rounded-t-[36px]",
          isCollapsed ? "pt-4" : "pt-4",
        )}
        style={{
          boxShadow: "0px -10px 30px -10px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* Selected template preview (admin only) */}
        {selectedTemplate && (
          <div className="px-5 pb-2 pt-4">
            <div className="rounded-lg border border-bordercolor-primary bg-surface-secondary p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-semibold text-txt-primary">
                  {selectedTemplate.name}
                </span>
                <button
                  type="button"
                  onClick={handleClearTemplate}
                  className="text-sm font-medium text-txt-brand transition-opacity active:opacity-70"
                >
                  {t("routes:conversations.template-cancel")}
                </button>
              </div>
              {selectedTemplate.previewText && typeof selectedTemplate.previewText === "string" && (
                <div className="text-sm leading-[1.4] text-txt-secondary">
                  {renderTemplateText(selectedTemplate.previewText)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Staged media preview */}
        {stagedMedia && !selectedTemplate && (
          <div className="px-5 pb-2 pt-4">
            <div className="relative inline-block">
              <img
                src={stagedMedia.previewUrl}
                alt={t("routes:conversations.attachment-image")}
                className="h-20 w-auto rounded-lg object-cover"
              />
              {/* Remove button */}
              <button
                type="button"
                onClick={handleRemoveStagedMedia}
                className="absolute -right-2 -top-2 flex size-6 items-center justify-center rounded-full bg-gray-800 text-white shadow-md transition-opacity active:opacity-70"
              >
                <IonIcon icon={xMarkIcon} className="size-4" />
              </button>
            </div>
          </div>
        )}

        {/* Uploading indicator */}
        {isUploadingMedia && (
          <div className="px-5 pb-2 pt-4">
            <div className="flex items-center gap-2 text-txt-secondary">
              <div className="size-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
              <span className="text-sm">{t("routes:conversations.uploading-image")}</span>
            </div>
          </div>
        )}

        {/* Text input area - hidden when collapsed or template selected */}
        {!selectedTemplate && (
          <div
            className={cx(
              "overflow-hidden px-5 transition-all duration-200",
              isCollapsed ? "max-h-0 opacity-0" : "max-h-24 opacity-100",
            )}
          >
            <div className="pl-1">
              <textarea
                value={input}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onFocus={handleExpand}
                onBlur={handleBlur}
                placeholder={
                  isDisabled
                    ? t("routes:conversations.cannot-send-freeform")
                    : t("routes:conversations.type-message")
                }
                disabled={isDisabled}
                className={cx(
                  "w-full resize-none border-0 bg-transparent py-2 text-base font-medium leading-[1.5] text-txt-primary placeholder:text-txt-tertiary focus:outline-none",
                  isDisabled && "cursor-not-allowed text-txt-secondary",
                )}
                rows={2}
              />
            </div>
          </div>
        )}

        {/* Bottom action bar */}
        <div className="flex items-center justify-between px-5 pb-2 pt-4">
          <div className="flex items-center gap-1.5">
            {/* Plus button - opens attachment modal (all users) */}
            <button
              type="button"
              className="flex size-10 items-center justify-center text-txt-primary transition-opacity active:opacity-70"
              onClick={openAttachmentModal}
              disabled={isUploadingMedia}
            >
              <IonIcon icon={plusIcon} className="size-4" />
            </button>

            {/* Template button - opens template selection (admin only) */}
            {isAdmin && (
              <button
                type="button"
                className="flex size-10 items-center justify-center text-txt-primary transition-opacity active:opacity-70"
                onClick={() => {
                  haptics.impact();
                  setIsTemplateModalOpen(true);
                }}
                disabled={!!selectedTemplate}
              >
                <IonIcon icon={documentTextIcon} className="size-5" />
              </button>
            )}

            {/* Platform selector button - only visible to admin users */}
            {isAdmin && (
              <button
                type="button"
                onClick={openPlatformModal}
                className="flex h-9 items-center gap-0.5 rounded-lg bg-brand-50 px-3 text-base font-medium text-txt-tertiary transition-opacity active:opacity-70"
              >
                <span>{platform}</span>
                <IonIcon
                  icon={chevronUpIcon}
                  className="size-[22px] rotate-180 text-txt-tertiary"
                />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Sparkles button (AI) - only visible to admin users */}
            {/* {isAdmin && (
              <button
                type="button"
                className="flex size-10 items-center justify-center text-txt-primary transition-opacity active:opacity-70"
                onClick={() => haptics.impact()}
              >
                <IonIcon icon={sparklesIcon} className="size-[22px]" />
              </button>
            )} */}

            {/* Send button */}
            <Button
              onClick={handleSend}
              disabled={!canSend}
              fill="clear"
              className={cx(
                "size-10 min-h-0 shrink-0 rounded-full part-native:size-10 part-native:min-h-0 part-native:rounded-full part-native:p-0",
                canSend
                  ? "bg-brand-500 text-txt-inverted"
                  : "bg-bordercolor-primary text-txt-primary",
              )}
            >
              <IonIcon
                slot="icon-only"
                icon={arrowUpIcon}
                className={cx("size-5", canSend ? "text-txt-inverted" : "text-txt-primary")}
              />
            </Button>
          </div>
        </div>
      </div>

      {/* Solid background extending to bottom edge */}
      <div
        className="bg-white"
        style={{
          height: `calc(max(env(safe-area-inset-bottom, 0px), var(--safe-area-inset-bottom, 0px)) + 16px)`,
        }}
      />

      {/* Platform selection modal */}
      <IonModal
        ref={modalRef}
        isOpen={isPlatformModalOpen}
        onDidDismiss={() => setIsPlatformModalOpen(false)}
        initialBreakpoint={0.35}
        breakpoints={[0, 0.35]}
        className="platform-modal"
      >
        <div className="rounded-3xl bg-white p-5">
          <p className="text-base font-medium leading-[1.5] text-txt-secondary">
            {t("routes:conversations.select-platform")}
          </p>

          <div className="mt-2 flex flex-col">
            <button
              type="button"
              onClick={() => handlePlatformSelect("Boomer")}
              className="flex items-center justify-between border-b border-bordercolor-primary px-2 py-4 transition-colors active:bg-gray-50"
            >
              <div className="flex items-center gap-3">
                <BoomerIcon width={48} height={48} />
                <span className="text-base font-medium leading-[1.5] text-txt-primary">Boomer</span>
              </div>
              {platform === "Boomer" && (
                <IonIcon icon={checkIcon} className="size-6 text-txt-primary" />
              )}
            </button>

            <button
              type="button"
              onClick={() => handlePlatformSelect("Whatsapp")}
              disabled={!canSendFreeform}
              className={cx(
                "flex items-center justify-between px-2 py-4 transition-colors",
                canSendFreeform ? "active:bg-gray-50" : "cursor-not-allowed opacity-50",
              )}
            >
              <div className="flex items-center gap-3">
                <WhatsappIcon width={48} height={48} />
                <span className="text-base font-medium leading-[1.5] text-txt-primary">
                  Whatsapp
                </span>
              </div>
              {platform === "Whatsapp" && (
                <IonIcon icon={checkIcon} className="size-6 text-txt-primary" />
              )}
            </button>
          </div>
        </div>
      </IonModal>

      {/* Attachment selection modal (all users) */}
      <IonModal
        ref={attachmentModalRef}
        isOpen={isAttachmentModalOpen}
        onDidDismiss={() => setIsAttachmentModalOpen(false)}
        initialBreakpoint={0.25}
        breakpoints={[0, 0.25]}
        className="attachment-modal"
      >
        <div className="rounded-3xl bg-white p-5">
          <p className="text-base font-medium leading-[1.5] text-txt-secondary">
            {t("routes:conversations.select-attachment")}
          </p>

          <div className="mt-2 flex flex-col">
            <button
              type="button"
              onClick={handlePickImage}
              className="flex items-center gap-3 px-2 py-4 transition-colors active:bg-gray-50"
            >
              <div className="flex size-12 items-center justify-center rounded-full bg-brand-50">
                <IonIcon icon={photoIcon} className="size-6 text-brand-500" />
              </div>
              <span className="text-base font-medium leading-[1.5] text-txt-primary">
                {t("routes:conversations.attachment-image")}
              </span>
            </button>
          </div>
        </div>
      </IonModal>

      {/* Template selection modal for admin users */}
      {isAdmin && (
        <TemplateSelectionModal
          isOpen={isTemplateModalOpen}
          onClose={() => setIsTemplateModalOpen(false)}
          platform={platform}
          onSelectTemplate={(template) => {
            setSelectedTemplate(template);
            setIsTemplateModalOpen(false);
          }}
        />
      )}
    </div>
  );
}
